import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { ChevronDown, ChevronUp, Copy, Check, Shield } from 'lucide-react'
import { Button } from '~/components/common/Button'
import { SettingsSection } from '~/components/common/SettingsSection'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import {
  changeSSOClientID,
  changeSSOClientSecret,
  changeSSOTenantID
} from '~/services/organizationService'
import { BetterToast } from '~/utils/toast'

interface SSOConfigurationsSectionProps {
  ssoOrgLink: string
  callBackLink: string
  SSOClientID: string
  setSSOClientID: (value: string) => void
  SSOClientSecret: string
  setSSOClientSecret: (value: string) => void
  SSOTenantID: string
  setSSOTenantID: (value: string) => void
}

export function SSOConfigurationsSection({
  ssoOrgLink,
  callBackLink,
  SSOClientID,
  setSSOClientID,
  SSOClientSecret,
  setSSOClientSecret,
  SSOTenantID,
  setSSOTenantID
}: SSOConfigurationsSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const { refetch, currentWorkspace } = useWorkspaceContext()

  const { mutate: updateClientID } = useMutation({
    mutationFn: changeSSOClientID,
    onSuccess: () => {
      refetch()
      BetterToast.success('Client ID Updated')
    }
  })

  const { mutate: updateClientSecret } = useMutation({
    mutationFn: changeSSOClientSecret,
    onSuccess: () => {
      refetch()
      BetterToast.success('Client Secret Updated')
    }
  })

  const { mutate: updateTenantId } = useMutation({
    mutationFn: changeSSOTenantID,
    onSuccess: () => {
      refetch()
      BetterToast.success('Tenant ID Updated')
    }
  })

  const onClientIDBlur = (value: string) => {
    if (!currentWorkspace?.uuid) return
    updateClientID({
      workspaceUuid: currentWorkspace.uuid,
      client_id: value,
      callback_url: callBackLink
    })
  }

  const onClientSecretBlur = (value: string) => {
    if (!currentWorkspace?.uuid) return
    updateClientSecret({
      workspaceUuid: currentWorkspace.uuid,
      client_secret: value,
      callback_url: callBackLink
    })
  }

  const onTenantIDBlur = (value: string) => {
    if (!currentWorkspace?.uuid) return
    updateTenantId({
      workspaceUuid: currentWorkspace.uuid,
      client_tenantid: value,
      callback_url: callBackLink
    })
  }

  return (
    <SettingsSection>
      {/* Header with expand/collapse */}
      <button
        type='button'
        onClick={() => setIsExpanded(!isExpanded)}
        className='flex items-center gap-4 w-full h-12 cursor-pointer'
      >
        <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-neutral-icon-solid'>
          <Shield className='w-[18px] h-[18px]' />
        </span>
        <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] flex-1 text-left px-2 select-none'>
          SSO Configuration
        </span>
        <span className='flex items-center justify-center w-8 h-8'>
          {isExpanded ? (
            <ChevronUp className='w-4 h-4 text-tokens-neutral-icon-solid' />
          ) : (
            <ChevronDown className='w-4 h-4 text-tokens-neutral-icon-solid' />
          )}
        </span>
      </button>

      {isExpanded && (
        <>
          <div className='border-t border-tokens-neutral-border-2' />

          <CopyableRow label='Log In Link' link={ssoOrgLink} />
          <CopyableRow label='Copy Link' link={callBackLink} />

          <SSOInputField
            label='Client ID'
            value={SSOClientID}
            onChange={setSSOClientID}
            onSave={onClientIDBlur}
          />
          <SSOInputField
            label='Client Secret'
            value={SSOClientSecret}
            onChange={setSSOClientSecret}
            onSave={onClientSecretBlur}
          />
          <SSOInputField
            label='Tenant ID'
            value={SSOTenantID}
            onChange={setSSOTenantID}
            onSave={onTenantIDBlur}
          />
        </>
      )}
    </SettingsSection>
  )
}

function CopyableRow({ label, link }: { label: string; link: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      BetterToast.error('Failed to copy link')
    }
  }

  return (
    <div className='flex items-center gap-4 h-12'>
      <div className='w-6 h-6 shrink-0' />
      <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] flex-1 min-w-0 px-2 select-none'>
        {label}
      </span>
      <div className='flex items-center gap-2 shrink-0'>
        <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.45)] truncate max-w-[300px]'>
          {link}
        </span>
        <Button
          variant='outlined'
          leftIcon={
            copied ? (
              <Check className='w-5 h-5 text-tokens-status-success-1' />
            ) : (
              <Copy className='w-5 h-5 text-tokens-neutral-icon-solid' />
            )
          }
          onClick={handleCopy}
          className='w-9 shrink-0'
          aria-label={`Copy ${label}`}
        />
      </div>
    </div>
  )
}

function SSOInputField({
  label,
  value,
  onChange,
  onSave
}: {
  label: string
  value: string
  onChange: (v: string) => void
  onSave: (v: string) => void
}) {
  return (
    <div className='flex items-center justify-between'>
      <div className='flex items-center gap-4'>
        <div className='w-6 h-6 shrink-0' />
        <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-2 select-none'>
          {label}
        </span>
      </div>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        onBlur={e => onSave(e.target.value)}
        className='w-[320px] h-12 px-3 py-2 bg-white border border-tokens-neutral-border-1 rounded-xl font-sans font-normal text-[14px] leading-[16px] tracking-[0.56px] text-[rgba(0,0,0,0.88)] outline-none focus:border-tokens-status-link-1 transition-colors'
        aria-label={label}
      />
    </div>
  )
}

export default SSOConfigurationsSection
